import React from "react";
import { observer } from "mobx-react";
import { Menu, Dropdown, Button, Input, Tag, Alert, Tooltip } from "antd";
import {
    LoadingOutlined,
    PlusCircleOutlined,
    HddOutlined,
    // UploadOutlined,
    FileOutlined,
    ReadOutlined,
    DeploymentUnitOutlined,
    InfoCircleOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import ArrowRight from "@app/assets/icons/arrow-right.js";
import SourceDocsPicker from "./source-docs";
import ArticlesPicker from "./articles-picker";
import { truncateName } from "./helper";

import articleState from "@app/state/store/report/article";
import chatStore from "../../state/store/ai-chat";

import "./style/conversation.scoped.scss";

const Prompt = observer(({ onPrompt, busy }) => {
    const files = chatStore.draftFiles;

    function setQuestion(value) {
        chatStore.setDraftQuestion(value);
    }

    function setFiles(newFiles) {
        chatStore.setDraftFiles(newFiles);
    }

    function ask() {
        if (!String(chatStore.draftQuestion).trim() || busy) {
            return;
        }

        const activeDocument = chatStore.activeDocument;

        const context = [
            ...(chatStore.isActiveContextEnabled && activeDocument
                ? [
                      {
                          _id: activeDocument?.id,
                          name: activeDocument?.name,
                          type: activeDocument?.type,
                          subType: activeDocument?.subType,
                      },
                  ]
                : []),
            ...files,
        ];

        onPrompt({
            question: chatStore.draftQuestion,
            context,
        });
    }

    function onFilesChange(newFiles) {
        setFiles(newFiles);
    }

    function onPressEnter(e) {
        if (!e.shiftKey) {
            e.preventDefault();
            ask();
        }
    }

    const isActiveDocumentArticle = chatStore.activeDocument?.type === "article";
    const isActiveDocumentReport = chatStore.activeDocument?.type === "report document";
    const title = isActiveDocumentArticle
        ? articleState?.article?.title
        : chatStore?.activeDocument?.name;

    const truncatedName = truncateName(title);
    const mdrId = isActiveDocumentArticle
        ? articleState?.article?.mdrId
        : chatStore?.activeDocument?.mdrId;

    const hasFile = isActiveDocumentArticle
        ? articleState?.article?.file
        : chatStore?.activeDocument?.hasFile;

    const activeLabel = isActiveDocumentReport
        ? truncatedName
        : `${mdrId ? `C${mdrId} - ` : ""}${truncatedName}`;

    const singleContextMessage =
        isActiveDocumentReport || hasFile
            ? `Context: ${activeLabel}`
            : "The full article is missing.";

    const hasActiveContext = chatStore.isActiveContextEnabled && !!chatStore.activeDocument;
    const totalContextCount = (hasActiveContext ? 1 : 0) + files.length;
    const isMultipleContext = totalContextCount > 1;
    const showContextAlert = hasActiveContext || files.length > 1;

    const contextItems = [...(hasActiveContext ? [title] : []), ...files.map((f) => f.name)];

    function clearAllContext() {
        chatStore.disableActiveContext();
        setFiles([]);
    }

    return (
        <div className="prompt">
            {showContextAlert && (
                <div className="context">
                    {isMultipleContext ? (
                        <Tooltip
                            title={
                                <div>
                                    {contextItems.map((name, i) => (
                                        <div key={i}>• {name}</div>
                                    ))}
                                </div>
                            }
                            placement="top"
                        >
                            <Alert
                                message={
                                    <>
                                        <div>Context: Multiple Files</div>
                                        {hasActiveContext && (
                                            <div className="current-document">
                                                Current document: {activeLabel}
                                            </div>
                                        )}
                                    </>
                                }
                                type="warning"
                                closable
                                closeIcon={<CloseOutlined />}
                                onClose={clearAllContext}
                            />
                        </Tooltip>
                    ) : (
                        <Alert
                            message={singleContextMessage}
                            type="warning"
                            closable
                            closeIcon={<CloseOutlined />}
                            onClose={clearAllContext}
                        />
                    )}
                </div>
            )}
            <div className="input">
                <Input.TextArea
                    autoSize={{ minRows: 2, maxRows: 7 }}
                    placeholder="Type your question to CAPTIS Copilot here..."
                    value={chatStore.draftQuestion}
                    onInput={(e) => setQuestion(e.currentTarget.value)}
                    onPressEnter={onPressEnter}
                    disabled={busy}
                />

                <div className="search-button">
                    {busy ? (
                        <Button type="icon" icon={<LoadingOutlined />} disabled />
                    ) : (
                        <Button type="icon" icon={<ArrowRight />} onClick={ask} />
                    )}
                </div>
            </div>

            <Files files={files} onFilesChange={onFilesChange} busy={busy} />

            <div className="ai-caution">
                <InfoCircleOutlined />
                <span>
                    AI-generated content may be inaccurate. Review and verify it before use.
                </span>
            </div>
        </div>
    );
});

const MenuInfo = ({ title }) => (
    <span style={{ marginLeft: 8, color: "#999" }} onClick={(e) => e.stopPropagation()}>
        <Tooltip placement="right" title={title}>
            <InfoCircleOutlined />
        </Tooltip>
    </span>
);

const FilesMenu = observer(({ onPickSourceDocs, onPickArticles }) => {
    const hasActiveDoc = !!chatStore.activeDocument;
    const isEnabled = chatStore.isActiveContextEnabled;

    return (
        <Menu>
            <Menu.Item icon={<HddOutlined />} onClick={onPickSourceDocs}>
                Source Documents
                <MenuInfo title="The list contains user-uploaded source documents." />
            </Menu.Item>
            {chatStore.dueEnabled && (
                <Menu.Item icon={<ReadOutlined />} onClick={() => onPickArticles("slr")}>
                    DUE Articles
                    <MenuInfo title="The list contains system-retrieved DUE literature articles." />
                </Menu.Item>
            )}
            {chatStore.sotaEnabled && (
                <Menu.Item icon={<DeploymentUnitOutlined />} onClick={() => onPickArticles("sota")}>
                    SoTA Articles
                    <MenuInfo title="The list contains system-retrieved SoTA literature articles." />
                </Menu.Item>
            )}
            {hasActiveDoc && isEnabled ? (
                <Menu.Item icon={<FileOutlined />} onClick={() => chatStore.disableActiveContext()}>
                    Remove current file
                </Menu.Item>
            ) : (
                <Menu.Item
                    icon={<FileOutlined />}
                    onClick={() => chatStore.enableActiveContext()}
                    disabled={!hasActiveDoc}
                >
                    Use current file
                </Menu.Item>
            )}
        </Menu>
    );
});

const Files = observer(({ files, onFilesChange, busy }) => {
    const [picker, setPicker] = React.useState(null);
    const [dropdownOpen, setDropdownOpen] = React.useState(false);

    function openPicker(name) {
        setDropdownOpen(false);
        setPicker(name);
    }

    function closePicker() {
        setPicker(null);
    }

    // Merge a picker's selection into the draft, replacing only the items it owns
    // so source documents, DUE and SoTA articles can be combined freely.
    function mergeSelection(ownsItem, selected) {
        const keep = files.filter((file) => !ownsItem(file));
        onFilesChange([...keep, ...selected]);
        closePicker();
    }

    const ownsSourceDoc = (file) => (file.type ?? "file") === "file";
    const ownsArticleType = (type) => (file) =>
        file.type === "article" && file.articleType === type;

    function onRemoveFile(fileId) {
        const newFileList = files.filter((file) => file._id !== fileId);
        onFilesChange(newFileList);
    }

    return (
        <>
            <div className="input-actions">
                <div className="files-header">
                    <div className="files">
                        <Dropdown
                            overlay={
                                <FilesMenu
                                    onPickSourceDocs={() => openPicker("docs")}
                                    onPickArticles={(type) => openPicker(type)}
                                />
                            }
                            trigger="click"
                            placement="topLeft"
                            visible={dropdownOpen}
                            onVisibleChange={setDropdownOpen}
                            onClick={(e) => e.stopPropagation()}
                            disabled={busy}
                        >
                            <Button icon={<PlusCircleOutlined />} disabled={busy}>
                                Add content
                            </Button>
                        </Dropdown>
                    </div>
                    {files.length > 0 && <label>Using:</label>}
                </div>
                {files.length > 0 && (
                    <div className="file-list">
                        {files.map((file) => {
                            const label = `${file.mdrId ? `C${file.mdrId} - ` : ""}${file.name}`;
                            const isArticle = file.type === "article";

                            return (
                                <Tooltip key={file._id} title={label} placement="top">
                                    <Tag closable onClose={() => onRemoveFile(file._id)}>
                                        {isArticle ? <ReadOutlined /> : <FileOutlined />}{" "}
                                        {truncateName(label, 30)}
                                    </Tag>
                                </Tooltip>
                            );
                        })}
                    </div>
                )}
            </div>

            {picker === "docs" && (
                <SourceDocsPicker
                    onCancel={closePicker}
                    onSelect={(selected) => mergeSelection(ownsSourceDoc, selected)}
                    files={files}
                />
            )}
            {(picker === "slr" || picker === "sota") && (
                <ArticlesPicker
                    type={picker}
                    onCancel={closePicker}
                    onSelect={(selected) => mergeSelection(ownsArticleType(picker), selected)}
                    files={files}
                />
            )}
        </>
    );
});

export default Prompt;
